import { Navigate, useLocation } from "react-router-dom";
import { type ReactNode } from "react";
import { useAuth } from "@/lib/auth";
import { useOwnerIdentity } from "@/lib/useOwnerIdentity";
import { useUserOrganizations } from "@/lib/org";
import { LoadingScreen } from "@/components/shell/LoadingScreen";

// Phase 6.12.2 - Owner route guard. Mirrors TenantRoute with owner semantics.
//
// Two modes:
//   requireLink = true (default): the real portal. Requires an authenticated
//     user who resolves to a LINKED owner (owner_clients.user_id = auth.uid()).
//     - not authenticated            -> /login
//     - authenticated, org member    -> /app/dashboard (manager, wrong portal)
//     - authenticated, no owner link -> /owner/welcome (likely mid-invite)
//   requireLink = false: the welcome/set-password landing. Requires only an
//     authenticated session; linking has not happened yet.
//
// Waits for both identity queries to fully settle before deciding, so routing
// never flickers during auth resolution.

interface OwnerRouteProps {
  children: ReactNode;
  requireLink?: boolean;
}

export function OwnerRoute({ children, requireLink = true }: OwnerRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const {
    data: owner,
    isLoading: ownerLoading,
    isFetching: ownerFetching,
    isSuccess: ownerLoaded,
  } = useOwnerIdentity();
  const {
    data: memberships,
    isLoading: orgsLoading,
    isFetching: orgsFetching,
    isSuccess: orgsLoaded,
  } = useUserOrganizations();

  if (authLoading) return <LoadingScreen />;
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  // Welcome/landing: session is enough; do not require an owner link yet.
  if (!requireLink) {
    return <>{children}</>;
  }
  // Real portal: wait for both identity queries to settle before deciding.
  if (
    ownerLoading || ownerFetching || !ownerLoaded ||
    orgsLoading || orgsFetching || !orgsLoaded
  ) {
    return <LoadingScreen />;
  }
  // Linked owner -> allow.
  if (owner) return <>{children}</>;
  // Not an owner. If they are an org member, send them to the manager app.
  if (memberships && memberships.length > 0) {
    return <Navigate to="/app/dashboard" replace />;
  }
  // Authenticated but neither owner nor org member: most likely a freshly
  // invited owner who has not completed linking. Send them to welcome.
  return <Navigate to="/owner/welcome" replace />;
}
